import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { 
  AlertTriangle, MessageSquare, FileText, Clock, 
  ArrowRight, Star, Users, TrendingUp, CheckCircle, X
} from 'lucide-react';
import { employerAPI, employeeAPI } from '../../services/api';
import { toast } from 'sonner';

const QuickActionsWidget = ({ userType = 'employer' }) => {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dismissing, setDismissing] = useState(null);
  const location = useLocation();

  const fetchActions = useCallback(async () => {
    try {
      setLoading(true);
      const api = userType === 'employer' ? employerAPI : employeeAPI;
      const response = await api.getQuickActions();
      setActions(response.data?.actions || []);
    } catch (error) {
      console.error('Error fetching quick actions:', error);
    } finally {
      setLoading(false);
    }
  }, [userType]);

  // Fetch on mount, route change, and window focus
  useEffect(() => {
    fetchActions();
  }, [fetchActions, location.pathname]);

  useEffect(() => {
    const handleFocus = () => fetchActions();
    window.addEventListener('focus', handleFocus);
    
    // Also refresh every 30 seconds
    const interval = setInterval(fetchActions, 30000);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
      clearInterval(interval);
    };
  }, [fetchActions]);

  const handleDismiss = async (actionKey, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      setDismissing(actionKey);
      const api = userType === 'employer' ? employerAPI : employeeAPI;
      await api.dismissQuickAction(actionKey);
      
      // Immediately remove from local state
      setActions(prev => prev.filter(a => a.action_key !== actionKey));
      toast.success('Marked as done');
    } catch (error) {
      console.error('Error dismissing action:', error);
      toast.error('Failed to dismiss');
    } finally {
      setDismissing(null);
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'pending_acknowledgment': return <FileText className="w-4 h-4" />;
      case 'overdue_reviews': return <AlertTriangle className="w-4 h-4" />;
      case 'upcoming_reviews': return <Clock className="w-4 h-4" />;
      case 'stale_pipeline': return <Users className="w-4 h-4" />;
      case 'score_improvement': return <TrendingUp className="w-4 h-4" />;
      default: return <Star className="w-4 h-4" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'high': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      case 'medium': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'low': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      default: return 'bg-[#2A2A4A] text-[#8888AA] border-[#3A3A5A]';
    }
  };

  if (loading) {
    return (
      <Card className="bg-[#16213E]/50 border-[#2A2A4A] p-4" data-testid="quick-actions-widget">
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-[#2A2A4A] rounded w-1/3"></div>
          <div className="h-12 bg-[#2A2A4A] rounded"></div>
          <div className="h-12 bg-[#2A2A4A] rounded"></div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="bg-[#16213E]/50 border-[#2A2A4A] p-4" data-testid="quick-actions-widget">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white font-semibold">Quick Actions</h3>
        {actions.length > 0 && (
          <Badge variant="secondary" className="bg-[#E94560]/20 text-[#E94560]">
            {actions.length}
          </Badge>
        )}
      </div>

      {actions.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-center" data-testid="quick-actions-empty">
          <CheckCircle className="w-12 h-12 text-green-500 mb-3" />
          <p className="text-white font-medium">You're all caught up.</p>
          <p className="text-[#8888AA] text-sm mt-1">No pending actions right now.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {actions.map((action) => (
            <Link 
              key={action.action_key || action.type}
              to={action.href}
              className="block group"
              data-testid={`quick-action-${action.type}`}
            >
              <div className={`p-3 rounded-lg border transition-all hover:border-[#E94560]/50 ${getPriorityColor(action.priority)}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="mt-0.5 flex-shrink-0">
                      {getIcon(action.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">{action.label}</p>
                      {action.items && action.items.length > 0 && (
                        <p className="text-[#8888AA] text-xs mt-1 truncate">
                          {action.items[0]?.employee_name || action.items[0]?.employer_name || action.items[0]?.name || 'View details'}
                          {action.items.length > 1 && ` +${action.items.length - 1} more`}
                        </p>
                      )}
                      {action.tips && action.tips.length > 0 && (
                        <p className="text-[#8888AA] text-xs mt-1 truncate">
                          {action.tips[0]?.tip}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => handleDismiss(action.action_key, e)}
                      disabled={dismissing === action.action_key}
                      className="h-7 px-2 text-xs text-[#8888AA] hover:text-white hover:bg-[#2A2A4A] opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`dismiss-${action.type}`}
                    >
                      {dismissing === action.action_key ? (
                        <span className="animate-spin">...</span>
                      ) : (
                        <>
                          <X className="w-3 h-3 mr-1" />
                          Done
                        </>
                      )}
                    </Button>
                    <ArrowRight className="w-4 h-4 text-[#8888AA] group-hover:text-[#E94560] transition-colors" />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </Card>
  );
};

export default QuickActionsWidget;
